import React, { useCallback, useEffect, useRef, useState } from "react";
import { Add } from "@material-symbols-svg/react";

export interface AddButtonOverlayOption {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

const OVERLAY_DURATION_MS = 300;
const BUTTON_SIZE = 48;
const OPTIONS_GAP_ABOVE_BUTTON = 16;

interface AddButtonOverlayProps {
  options: AddButtonOverlayOption[];
  /** Distance from bottom (px). Default 80 (similar to bottom-20). */
  bottomOffset?: number;
  /** Distance from right (px). Default 16 (same as right-4). */
  rightOffset?: number;
}

const AddButtonOverlay: React.FC<AddButtonOverlayProps> = ({
  options,
  bottomOffset = 80,
  rightOffset = 16,
}) => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (overlayOpen) {
      document.body.style.overflow = "hidden";
      setOverlayVisible(false);
      const frame = requestAnimationFrame(() => {
        setOverlayVisible(true);
      });
      return () => {
        cancelAnimationFrame(frame);
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [overlayOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const closeOverlay = useCallback(() => {
    if (!overlayOpen) return;
    setOverlayVisible(false);
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      setOverlayOpen(false);
    }, OVERLAY_DURATION_MS);
  }, [overlayOpen]);

  const toggleOverlay = useCallback(() => {
    if (overlayOpen) {
      closeOverlay();
    } else {
      setOverlayOpen(true);
    }
  }, [overlayOpen, closeOverlay]);

  const optionsBottom = bottomOffset + BUTTON_SIZE + OPTIONS_GAP_ABOVE_BUTTON;

  return (
    <>
      <button
        type="button"
        onClick={toggleOverlay}
        className="fixed z-20 w-12 h-12 bg-[var(--app-primary)] rounded-full flex items-center justify-center shadow-lg text-white transition-transform duration-300 ease-out"
        style={{
          bottom: `${bottomOffset}px`,
          right: `${rightOffset}px`,
          transform: overlayOpen ? "rotate(45deg)" : "rotate(0deg)",
        }}
        aria-label={overlayOpen ? "Close" : "Open options"}
      >
        <Add className="w-6 h-6" />
      </button>

      {overlayOpen && (
        <div
          className={`fixed inset-0 z-10 bg-black/50 transition-all duration-300 ease-out ${
            overlayVisible
              ? "opacity-100 backdrop-blur-md"
              : "opacity-0 backdrop-blur-none"
          }`}
          aria-hidden
        >
          <button
            type="button"
            className="absolute inset-0"
            onClick={closeOverlay}
            aria-label="Close overlay"
          />
          <div
            className="absolute left-4 flex flex-col items-end gap-3"
            style={{
              bottom: `${optionsBottom}px`,
              right: `${rightOffset}px`,
            }}
          >
            {options.map((opt, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setTimeout(() => {
                    opt.onClick?.();
                    closeOverlay();
                  }, 100);
                }}
                className="group flex items-center gap-3 w-full justify-end max-w-xs py-1 rounded-lg transition-transform duration-200 ease-out active:scale-105"
              >
                <span className="text-white font-medium text-sm transition-all duration-200 ease-out group-active:text-base">
                  {opt.label}
                </span>
                <span className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-gray-700 shadow shrink-0 transition-all duration-200 ease-out group-active:w-14 group-active:h-14 [&_svg]:transition-all [&_svg]:duration-200 [&_svg]:ease-out [&_svg]:w-5 [&_svg]:h-5 group-active:[&_svg]:w-6 group-active:[&_svg]:h-6">
                  {opt.icon}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AddButtonOverlay;
